import { useState, useEffect, useRef } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { multiFilterChange } from "../../../utils";
import { AcpService } from "../../../service/ACPService";
import { AcpCommonService } from "../../../service/AcpCommonService";
import { SortAndFilterService } from "../../../service/SortAndFilterService";
import ACPFilter from "../../ACP/common/ACPFilter";
import filter from "../../../assets/images/filter.svg";
import blueFilter from "../../../assets/images/BlueFilterIcon.svg";

const CommonGrid = ({
  data = [],
  columns = [],
  mode = "max",
  paginator,
  rows = 20,
  filters,
  globalFilterFields,
  dataKey,
  emptyMessage = "No data found.",
  tableProps = {},
  className = "",
  showACPFilter = false,
  pageName = "",
  onPageChange,
  lazy = false,
  totalRecords = 0,
}) => {
  const dt = useRef(null);
  const op = useRef(null);
  const isMax = mode === "max";

  // Filter state management
  const [pegadata, setPegadata] = useState([]);
  const [selectedFields, setSelectedFields] = useState({});
  const [sortData, setSortData] = useState([]);
  const [selectedColumnName, setSelectedColumnName] = useState(null);

  // Pagination state management
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(rows);
  const [currentPage, setCurrentPage] = useState(0);

  // Refs for filter tracking
  const filterArray = useRef([]);
  const colAllSelected = useRef([]);
  const selectedFieldsRef = useRef([]);
  const selectedColumnList = useRef([]);

  // Initialize pegadata when data changes
  useEffect(() => {
    setPegadata(data);
    // Reset pagination when data changes
    setFirst(0);
    setCurrentPage(0);
  }, [data]);

  // Update pageSize when rows prop changes
  useEffect(() => {
    setPageSize(rows);
  }, [rows]);

  // Apply filters when selectedFields changes
  useEffect(() => {
    if (selectedFields && Object.keys(selectedFields).length > 0) {
      const filteredData = applyFilterLogic(data, selectedFields);
      setPegadata(sortData?.length ? performSort(filteredData, sortData[0], sortData[1]) : filteredData);
    } else {
      setPegadata(sortData?.length ? performSort(data, sortData[0], sortData[1]) : data);
    }
  }, [selectedFields, data, sortData]);

  // Apply column filters to data (for flat DataTable data)
  const applyFilterLogic = (dataToFilter, uniqueObj) => {
    try {

      if (!dataToFilter?.length || !uniqueObj || Object.keys(uniqueObj).length === 0) {
        return dataToFilter;
      }

      // Separate CIC/FA filters and numeric columns from other filters
      const { CICFlag, FAFlag,  ...otherFilters } = uniqueObj;

      // First apply non-CIC/FA/numeric filters (these use AND logic)
      let filteredData = dataToFilter;

      if (Object.keys(otherFilters).length > 0) {
        filteredData = dataToFilter.filter((item) => {
          return Object.keys(otherFilters).every((fieldName) => {
            const filterValues = otherFilters[fieldName];
            const itemValue = item[fieldName];

            if (Array.isArray(filterValues)) {
              return filterValues.includes(itemValue);
            }
            return itemValue === filterValues;
          });
        });
      }

      // Apply CIC filter independently (if selected)
      if (CICFlag && Array.isArray(CICFlag) && CICFlag.length > 0) {
        filteredData = filteredData.filter((item) => {
          return CICFlag.includes(item.CICFlag);
        });
      }

      // Apply FA filter independently (if selected)
      if (FAFlag && Array.isArray(FAFlag) && FAFlag.length > 0) {
        filteredData = filteredData.filter((item) => {
          return FAFlag.includes(item.FAFlag);
        });
      }

      return filteredData;
    } catch (error) {
      return dataToFilter;
    }
  };

  // Normalize data structure for sorting - wrap flat data in .data property if needed
  const performSort = (dataToSort, column, direction) => {
    if (!dataToSort?.length) return dataToSort;
    const isAlreadyNormalized = dataToSort[0]?.data !== undefined;
    const normalized = isAlreadyNormalized ? dataToSort : dataToSort.map(item => ({ data: item }));
    const sorted = SortAndFilterService.sortMultiColumnData(normalized, column, direction);
    return isAlreadyNormalized ? sorted : sorted.map(item => item.data);
  };

  // Handle sort
  const onSort = (column, direction) => {
    if (pegadata?.length > 0) {
      setSortData([column, direction]);
      setPegadata(performSort(pegadata, column, direction));
      // Reset pagination on sort
      setFirst(0);
      setCurrentPage(0);
    }
  };

  // Handle page change
  const onPageChangeHandler = (event) => {
    const newFirst = event.first || 0;
    const newPage = Math.floor(newFirst / event.rows);
    setFirst(newFirst);
    setCurrentPage(newPage);
    setPageSize(event.rows);

    // Call parent callback if provided
    if (onPageChange && typeof onPageChange === "function") {
      onPageChange({
        first: newFirst,
        rows: event.rows,
        page: newPage,
        totalRecords: lazy ? totalRecords : pegadata?.length || 0,
      });
    }
  };

  // Handle global filter change (from ACPFilter)
  const onGlobalFilterChange = (e, colName) => {
    
    const { value, multiplesFlag, arr } = multiFilterChange(
      e,
      colName,
      selectedColumnName,
      selectedFields,
      setSelectedFields,
      data,
      filterArray,
      selectedColumnList,
      colAllSelected,
      selectedFieldsRef
    );

    // Reset pagination when filter is applied
    setFirst(0);
    setCurrentPage(0);

    // Case 1: All items deselected from this column
    if (value?.length === 0) {
      setSelectedFields({});
      selectedFieldsRef.current = [];
      selectedColumnList.current = [];
      filterArray.current = [];
      colAllSelected.current = [];
      return;
    }

    // Process the filter arrays
    const _selectedFieldsRef = (selectedFieldsRef?.current || [])?.map((el) => {
      if (Object.keys(arr)?.[0] === Object.keys(el)?.[0]) {
        return {
          ...el,
          [`${selectedColumnName}`]: arr[selectedColumnName],
        };
      } else {
        return el;
      }
    });

    // Case 2: No filters exist at all
    if (!filterArray?.current?.length) {
      setSelectedFields({});
      selectedFieldsRef.current = [];
      selectedColumnList.current = [];
      return;
    }

    // Case 3: All filter fields are empty
    if (AcpService.areAllPropertiesEmptyArrays(_selectedFieldsRef)) {
      setSelectedFields({});
      selectedFieldsRef.current = [];
      selectedColumnList.current = [];
      return;
    }

    // Case 4: Apply filters if valid selections exist
    if ((value?.length > 0 && !multiplesFlag) || colAllSelected?.current?.length >= 1) {
      const _selectedFieldsRefWithoutEmptyArray = AcpService.removeEmptyArrays(_selectedFieldsRef);
      const _selectedFieldsRefWithUniqueObjects = AcpService.getUniqueObjects(
        _selectedFieldsRefWithoutEmptyArray
      );
      const convertedObj = AcpCommonService.convertArrayToObject(_selectedFieldsRefWithUniqueObjects);
      const uniqueObj = AcpCommonService.getUniqueValuesObject(convertedObj);
      setSelectedFields(uniqueObj);
    } else {
    }
  };

  // Clear column-wise filter
  const clearColumnWiseFilter = () => {
    setSelectedFields({});
    filterArray.current = [];
    selectedFieldsRef.current = [];
    selectedColumnList.current = [];
    colAllSelected.current = [];
    setSortData([]);
    setSelectedColumnName(null);
    // Reset pagination on filter clear
    setFirst(0);
    setCurrentPage(0);
    op.current?.hide();
  };

  // Header with filter icon
  const columnHeader = (columnField, columnHeader) => {
    return (
      <div className="flex-display">
        <img
          src={selectedFields?.[columnField]?.length > 0 ? blueFilter : filter}
          alt="Column Filter"
          onClick={(e) => {
            op.current?.toggle(e);
            setSelectedColumnName(columnField);
          }}
          className="columnFilterIcon"
          style={{ cursor: "pointer" }}
        />
        <span className="columnFilterIcon">{columnHeader}</span>
      </div>
    );
  };

  return (
    <>
      {showACPFilter && isMax && (
        <ACPFilter
          op={op}
          filters={null}
          onSort={onSort}
          sortData={sortData}
          pegadata={pegadata}
          setFilters={setPegadata}
          setSortData={setSortData}
          projectData={data}
          saveSettings={() => { }}
          generateCustomOptions={true}
          isTreeTable={false}
          pageName={pageName}
          frozenCoulmns={[]}
          selectedFields={selectedFields}
          selectAllColumnNotIncluded={true}
          selectedColumnName={selectedColumnName}
          onGlobalFilterChange={onGlobalFilterChange}
          clearColumnWiseFilter={clearColumnWiseFilter}
        />
      )}
      <DataTable
        ref={dt}
        value={pegadata ?? []}
        showGridlines
        stripedRows={isMax ? false : true}
        scrollable={true}
        scrollHeight={isMax ? "auto" : "flex"}
        resizableColumns
        columnResizeMode="expand"
        paginator={typeof paginator === "boolean" ? paginator : isMax}
        first={first}
        rows={pageSize}
        onPage={onPageChangeHandler}
        totalRecords={lazy ? totalRecords : pegadata?.length || 0}
        lazy={lazy}
        rowsPerPageOptions={[5, 10, 20, 50, 100]}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown "
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
        filters={isMax ? filters : undefined}
        globalFilterFields={isMax ? globalFilterFields : undefined}
        dataKey={dataKey}
        emptyMessage={emptyMessage}
        size={isMax ? undefined : "small"}
        className={`${className} awm-dashboard-grid projectTable myProjectAnddAllProjectList`}
        {...tableProps}
      >
        {columns?.map?.((col) => (
          <Column
            key={col?.field}
            field={col?.field}
            header={showACPFilter && isMax ? columnHeader(col?.field, col?.header) : col?.header}
            hidden={col?.hidden}
            style={{ width: col?.width }}
            frozen={col?.frozen ?? false}
            body={col?.body}

            showFilterMenu={isMax ? col?.showFilterMenu : false}
            filterPlaceholder={isMax ? col?.filterPlaceholder : undefined}
          />
        ))}
      </DataTable>
    </>
  );
};

export default CommonGrid;
